import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  Search as SearchIcon,
  FileDownload as DownloadIcon,
  Description as VisualIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReportTransactionsTable from '../components/reports/ReportTransactionsTable';
import { reportsAPI } from '../api/reports';
import { LoadingSpinner } from '../components/common';
import { downloadExcelFile } from '../utils/fileDownload';
import {
  setDailyReportRange,
  setDailyReportData,
  clearDailyReport,
  selectDailyReportStartDate,
  selectDailyReportEndDate,
  selectDailyReportData
} from '../store/dailyReportSlice';

const VIEWER_ROUTES = {
  cash_sale: '/view/cash-sale',
  refund: '/view/refund',
  invoice: '/view/invoice',
  credit_note: '/view/credit-note'
};

export default function DailyReport() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const startDateIso = useSelector(selectDailyReportStartDate);
  const endDateIso = useSelector(selectDailyReportEndDate);
  const reportData = useSelector(selectDailyReportData);
  const startDate = startDateIso ? new Date(startDateIso) : null;
  const endDate = endDateIso ? new Date(endDateIso) : null;
  const [loading, setLoading] = useState(false);

  // Same pattern as Search.js: the date range/report data live in Redux so a
  // Daily Report -> Viewer -> back round trip restores the previous report
  // instead of forcing another API call, but shouldn't survive navigating
  // anywhere else - so only handleView flips this ref, and unmount clears
  // the stored report otherwise.
  const navigatingToViewerRef = useRef(false);

  useEffect(() => {
    return () => {
      if (!navigatingToViewerRef.current) {
        dispatch(clearDailyReport());
      }
    };
  }, [dispatch]);

  const setStartDate = (date) => {
    dispatch(setDailyReportRange({ startDate: date ? date.toISOString() : null, endDate: endDateIso }));
  };

  const setEndDate = (date) => {
    dispatch(setDailyReportRange({ startDate: startDateIso, endDate: date ? date.toISOString() : null }));
  };

  const handleSearch = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (startDate > endDate) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    try {
      const params = {
        start: format(startDate, "yyyy-MM-dd HH:mm:ss"),
        end: format(endDate, "yyyy-MM-dd HH:mm:ss")
      };

      const response = await reportsAPI.getDailyReport(params);
      dispatch(setDailyReportData(response.data));
      toast.success('Report loaded successfully');
    } catch (error) {
      console.error('Report error:', error);
      toast.error('Failed to load report');
      dispatch(setDailyReportData(null));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSage = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    try {
      const params = {
        start: format(startDate, "yyyy-MM-dd HH:mm:ss"),
        end: format(endDate, "yyyy-MM-dd HH:mm:ss")
      };

      const response = await reportsAPI.downloadDailyReport(params);
      const startStr = format(startDate, 'd-MMM-yy HH:mm');
      const endStr = format(endDate, 'd-MMM-yy HH:mm');
      const filename = `Daily Report ${startStr} to ${endStr}.xlsx`;

      downloadExcelFile(response.data, filename);
      toast.success('Sage export downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Sage export');
    }
  };

  const handleDownloadVisual = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    try {
      const params = {
        start: format(startDate, "yyyy-MM-dd HH:mm:ss"),
        end: format(endDate, "yyyy-MM-dd HH:mm:ss")
      };

      const response = await reportsAPI.downloadVisualReport(params);
      const startStr = format(startDate, 'd-MMM-yy HH:mm');
      const endStr = format(endDate, 'd-MMM-yy HH:mm');
      const filename = `Daily Report Visual ${startStr} to ${endStr}.xlsx`;

      downloadExcelFile(response.data, filename);
      toast.success('Visual report downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download visual report');
    }
  };

  const handleView = (transactionType, transaction) => {
    const route = VIEWER_ROUTES[transactionType];
    if (route) {
      navigatingToViewerRef.current = true;
      navigate(`${route}/${transaction.id}`, { state: { from: 'daily-report' } });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Daily Report
        </Typography>

        {/* Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <DateTimePicker
                label="Start Date & Time"
                value={startDate}
                onChange={setStartDate}
                format="dd/MM/yyyy HH:mm"
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <DateTimePicker
                label="End Date & Time"
                value={endDate}
                onChange={setEndDate}
                format="dd/MM/yyyy HH:mm"
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSage}
                  disabled={!reportData}
                >
                  Sage
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<VisualIcon />}
                  onClick={handleDownloadVisual}
                  disabled={!reportData}
                >
                  Visual
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Loading State */}
        {loading && <LoadingSpinner />}

        {/* Report Results */}
        {!loading && reportData && (
          <Box>
            <ReportTransactionsTable
              title="Cash Sales"
              data={reportData.cash_sales}
              totals={{
                subtotal: reportData.totals?.cash_sales_subtotal,
                vat: reportData.totals?.cash_sales_vat,
                total: reportData.totals?.cash_sales_total
              }}
              onView={(transaction) => handleView('cash_sale', transaction)}
            />

            <ReportTransactionsTable
              title="Refunds"
              data={reportData.refunds}
              totals={{
                subtotal: reportData.totals?.refunds_subtotal,
                vat: reportData.totals?.refunds_vat,
                total: reportData.totals?.refunds_total
              }}
              onView={(transaction) => handleView('refund', transaction)}
            />

            <ReportTransactionsTable
              title="Account Invoices"
              data={reportData.invoices}
              totals={{
                subtotal: reportData.totals?.invoices_subtotal,
                vat: reportData.totals?.invoices_vat,
                total: reportData.totals?.invoices_total
              }}
              onView={(transaction) => handleView('invoice', transaction)}
            />

            <ReportTransactionsTable
              title="Credit Notes"
              data={reportData.credit_notes}
              totals={{
                subtotal: reportData.totals?.credit_notes_subtotal,
                vat: reportData.totals?.credit_notes_vat,
                total: reportData.totals?.credit_notes_total
              }}
              onView={(transaction) => handleView('credit_note', transaction)}
            />
          </Box>
        )}

        {/* Empty State */}
        {!loading && !reportData && (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Select a date range and click Search to view the daily report
            </Typography>
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
}
